/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-23
 */

import React, { useState, useCallback } from 'react';
import { Box, Button, Typography, Alert, Paper, Divider } from '@mui/material';
import { Save as SaveIcon, Cancel as CancelIcon } from '@mui/icons-material';

import {
  StudioCreateFormProps,
  StudioFormData,
  StudioFormErrors,
} from '../../../types/studioForms';
import {
  createInitialStudioFormData,
  validateStudioForm,
  isStudioFormValid,
  validateBusinessInfoField,
} from '../../../utils/studioValidation';
import { StudioFormFields } from './StudioFormFields';
import { useSubscriptionLimits } from '../../../hooks/useSubscriptionLimits';
import LimitWarning from '../../subscription/LimitWarning';
import UpgradePrompt from '../../subscription/UpgradePrompt';

export const StudioCreateForm: React.FC<StudioCreateFormProps> = ({
  onSubmit,
  onCancel,
  loading = false,
  error = null,
}) => {
  // Subscription limits
  const {
    canCreateStudio,
    remainingStudios,
    isAtStudioLimit,
    isLoading: limitsLoading,
  } = useSubscriptionLimits();

  // Form state
  const [formData, setFormData] = useState<StudioFormData>(
    createInitialStudioFormData()
  );
  const [errors, setErrors] = useState<StudioFormErrors>({});

  // Upgrade prompt state
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [upgradePromptData, setUpgradePromptData] = useState<{
    currentUsage?: any;
    upgradeSuggestions?: any[];
  }>({});

  // Handle field changes
  const handleFieldChange = useCallback(
    (field: keyof StudioFormData, value: any) => {
      setFormData(prev => ({
        ...prev,
        [field]: value,
      }));

      // Clear error when user starts typing
      if (field !== 'businessInfo' && errors[field as keyof StudioFormErrors]) {
        setErrors(prev => ({
          ...prev,
          [field]: undefined,
        }));
      }

      // Real-time validation for business info fields
      if (field === 'businessInfo' && typeof value === 'object') {
        const newErrors = { ...errors };
        Object.entries(value).forEach(([subField, subValue]) => {
          if (typeof subValue === 'string') {
            const fieldError = validateBusinessInfoField(
              subField as 'address' | 'phone' | 'website',
              subValue
            );
            if (fieldError) {
              newErrors[subField as keyof StudioFormErrors] = fieldError;
            } else {
              delete newErrors[subField as keyof StudioFormErrors];
            }
          }
        });
        setErrors(newErrors);
      }
    },
    [errors]
  );

  // Handle form submission
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    // Check subscription limits first
    if (!canCreateStudio) {
      // Show upgrade prompt when user tries to create beyond limit
      setUpgradePromptData({});
      setShowUpgradePrompt(true);
      return;
    }

    // Validate entire form
    const validationErrors = validateStudioForm(formData);
    setErrors(validationErrors);

    // Check if form is valid
    if (!isStudioFormValid(validationErrors)) {
      return;
    }

    try {
      await onSubmit(formData);
    } catch (submitError: any) {
      // Handle 402 Payment Required errors
      if (submitError?.status === 402) {
        const errorData = submitError?.data;
        setUpgradePromptData({
          currentUsage: errorData?.current_usage,
          upgradeSuggestions: errorData?.upgrade_suggestions,
        });
        setShowUpgradePrompt(true);
      } else {
        // Other errors are handled by parent component
        console.error('Form submission error:', submitError);
      }
    }
  };

  // Handle cancel
  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
  };

  // Check if form has changes
  const hasChanges = () => {
    const initial = createInitialStudioFormData();
    return (
      formData.name !== initial.name ||
      formData.description !== initial.description ||
      formData.logo !== initial.logo ||
      JSON.stringify(formData.businessInfo) !==
        JSON.stringify(initial.businessInfo)
    );
  };

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
      {/* Header */}
      <Typography variant="h4" gutterBottom>
        Create New Studio
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Set up your studio profile to start managing your dance classes and
        members.
      </Typography>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Subscription Limit Warning */}
      {!limitsLoading && (
        <LimitWarning
          type="studio"
          currentCount={
            remainingStudios === 0 ? 0 : Math.max(0, remainingStudios - 1)
          }
          maxCount={remainingStudios === 0 ? 0 : remainingStudios}
          customMessage={(() => {
            if (isAtStudioLimit) {
              return "You've reached your studio limit. Upgrade your plan to create more studios.";
            }
            if (remainingStudios <= 2 && remainingStudios > 0) {
              return `You have ${remainingStudios} studio${remainingStudios !== 1 ? 's' : ''} remaining on your current plan.`;
            }
            return undefined;
          })()}
          sx={{ mb: 3 }}
        />
      )}

      {/* Form */}
      <Paper elevation={1} sx={{ p: 4 }}>
        <form onSubmit={handleSubmit}>
          <StudioFormFields
            formData={formData}
            onChange={handleFieldChange}
            errors={errors}
            disabled={loading}
          />

          <Divider sx={{ my: 4 }} />

          {/* Form Actions */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 2,
              mt: 4,
            }}
          >
            <Button
              variant="outlined"
              onClick={handleCancel}
              disabled={loading}
              startIcon={<CancelIcon />}
            >
              Cancel
            </Button>

            <Button
              type="submit"
              variant="contained"
              disabled={loading || !hasChanges() || !canCreateStudio}
              startIcon={<SaveIcon />}
              sx={{
                minWidth: 120,
              }}
            >
              {loading ? 'Creating...' : 'Create Studio'}
            </Button>
          </Box>
        </form>
      </Paper>

      {/* Help Text */}
      <Box sx={{ mt: 3 }}>
        <Typography variant="body2" color="text.secondary">
          <strong>What happens next?</strong>
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          • Your studio will be created and you&apos;ll be redirected to the
          studio dashboard
        </Typography>
        <Typography variant="body2" color="text.secondary">
          • You can invite staff to join your studio and assign roles
        </Typography>
        <Typography variant="body2" color="text.secondary">
          • All information can be updated later in studio settings
        </Typography>
      </Box>

      {/* Upgrade Prompt Dialog */}
      <UpgradePrompt
        open={showUpgradePrompt}
        onClose={() => setShowUpgradePrompt(false)}
        limitType="studio"
        currentUsage={upgradePromptData.currentUsage}
        upgradeSuggestions={upgradePromptData.upgradeSuggestions}
      />
    </Box>
  );
};

export default StudioCreateForm;
